import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useFleetAuth } from "../auth/fleetAuth";

interface PermissionsContextType {
  permissions: string[];
  hasPermission: (permission: string) => boolean;
  isLoading: boolean;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(
  undefined,
);

function decodeTokenPermissions(token: string): string[] {
  try {
    const payload = token.split(".")[1];
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    // JWT payloads are base64url-encoded without padding; atob requires
    // length to be a multiple of 4, so re-add `=` padding before decoding.
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "=",
    );
    const decoded = JSON.parse(atob(padded));
    return decoded.permissions ?? [];
  } catch {
    console.error("Failed to decode permissions from access token");
    return [];
  }
}

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    isLoading: providerIsLoading,
    isAuthenticated,
    getAccessToken,
  } = useFleetAuth();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Auth0 restores the session asynchronously, so its very first render is
    // always `isLoading: true, isAuthenticated: false`. Treating that as
    // "authenticated: no" published a *confident* empty permission set — and
    // consumers can't distinguish "no permissions" from "not known yet", so:
    //   - Navbar dropped every permissioned link on each load, then popped
    //     them back in once the token arrived (the reload flicker), and
    //   - RequirePermission redirected an authorized user away from /flights
    //     on a hard load. `<Navigate>` commits in a layout effect (child) which
    //     runs BEFORE this provider's passive effect (ancestor), so the bogus
    //     redirect always won the race.
    // Stay loading until Auth0 itself has settled.
    if (providerIsLoading) {
      setIsLoading(true);
      return;
    }

    if (!isAuthenticated) {
      setPermissions([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const loadPermissions = async () => {
      setIsLoading(true);
      try {
        const token = await getAccessToken();
        if (!cancelled) {
          setPermissions(decodeTokenPermissions(token));
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        console.error("Failed to load permissions:", message);
        if (!cancelled) {
          setPermissions([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadPermissions();

    return () => {
      cancelled = true;
    };
  }, [providerIsLoading, isAuthenticated, getAccessToken]);

  const value = useMemo(
    () => ({
      permissions,
      hasPermission: (permission: string) => permissions.includes(permission),
      isLoading,
    }),
    [permissions, isLoading],
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }
  return context;
};
